/**
 * 演画元素插件注册系统
 *
 * 新增元素类型只需调用 registerElementPlugin() 一次，无需散改多处文件。
 * 现有 ELEMENT_COMPONENTS / getHandlesComponent 均基于此注册表实现。
 */

import type { ComponentType } from 'react';
import type { Element, ElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import type { HandlesProps } from '@/components/canvas/selection/handles/types';

// ─── plugin interface ─────────────────────────────────────────────────────────

export interface ElementPlugin<T extends Element = Element> {
  /** 元素 type 字符串，唯一标识 */
  type: string;
  /** 用于 UI 展示的中文名称 */
  displayName: string;
  /** 画布渲染组件 */
  Component: ComponentType<ElementComponentProps<T>>;
  /** 交互把手组件，缺省则使用 RectHandles */
  HandlesComponent?: ComponentType<HandlesProps>;
  /**
   * Canvas 2D 导出渲染函数（可选，缺省则跳过此元素类型的导出渲染）。
   * 已在 core/export 中统一实现，此字段为可选扩展点。
   */
  drawToCanvas?: (
    ctx: CanvasRenderingContext2D,
    element: T,
    scale: number
  ) => Promise<void> | void;
}

// ─── registry ─────────────────────────────────────────────────────────────────

const _registry = new Map<string, ElementPlugin>();

export function registerElementPlugin(plugin: ElementPlugin): void {
  _registry.set(plugin.type, plugin);
}

export function getElementPlugin(type: string): ElementPlugin | undefined {
  return _registry.get(type);
}

export function getAllRegisteredTypes(): string[] {
  return Array.from(_registry.keys());
}

/**
 * 获取所有已注册元素的渲染组件 map（向后兼容 ELEMENT_COMPONENTS 格式）。
 * group 由 Workspace 单独渲染，不在此 map 中。
 */
export function getElementComponents(): Record<string, ComponentType<ElementComponentProps>> {
  const result: Record<string, ComponentType<ElementComponentProps>> = {};
  for (const [type, plugin] of _registry.entries()) {
    if (type !== 'group') {
      result[type] = plugin.Component as ComponentType<ElementComponentProps>;
    }
  }
  return result;
}

/**
 * 获取指定元素类型的把手组件（向后兼容 getHandlesComponent 格式）。
 */
export function getHandlesComponentForType(
  elementType: ElementType,
  fallback: ComponentType<HandlesProps>
): ComponentType<HandlesProps> {
  const plugin = _registry.get(elementType);
  return plugin?.HandlesComponent ?? fallback;
}
